/**
 * LaborSection.tsx — Mano de obra por lote dentro de Maquinaria (/maquinaria)
 *
 * Lista las jornadas del lote elegido y permite registrar una nueva.
 * Usa GET/POST /api/lotes/:loteId/labor (labor.handler.ts).
 */

import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle,
  Dropdown, Field, Input, Option, Spinner, Table, TableBody, TableCell, TableHeader,
  TableHeaderCell, TableRow, Text, tokens,
} from '@fluentui/react-components';
import { useT } from '../../i18n';
import { DateInput } from '../shared/DateInput';
import { NumberInput } from '../shared/NumberInput';
import { fetchLaborByLote, createLabor, type LaborType, type CreateLaborInput } from '../../api/labor.api';
import type { Lote } from '../../types/api.types';

const LABOR_TYPES: LaborType[] = ['JORNAL', 'DESTAJO', 'CONTRATO'];

interface LaborForm {
  date:               string;
  laborType:          LaborType;
  activity:           string;
  workers:            string;
  hoursPerWorker:     string;
  dailyRatePerWorker: string;
  notes:              string;
}

const today = () => new Date().toISOString().slice(0, 10);

const EMPTY_FORM: LaborForm = {
  date: today(), laborType: 'JORNAL', activity: '', workers: '1',
  hoursPerWorker: '8', dailyRatePerWorker: '', notes: '',
};

function toInput(f: LaborForm): CreateLaborInput {
  return {
    date:               f.date,
    laborType:          f.laborType,
    activity:           f.activity.trim(),
    workers:            Number(f.workers),
    hoursPerWorker:     Number(f.hoursPerWorker),
    dailyRatePerWorker: Number(f.dailyRatePerWorker),
    notes:              f.notes.trim() || undefined,
  };
}

function isValid(f: LaborForm): boolean {
  const workers = Number(f.workers);
  const hours = Number(f.hoursPerWorker);
  const rate = Number(f.dailyRatePerWorker);
  return Boolean(f.date && f.activity.trim())
    && Number.isInteger(workers) && workers > 0
    && hours > 0 && hours <= 24
    && f.dailyRatePerWorker !== '' && rate >= 0;
}

export function LaborSection({ lotes }: { lotes: Lote[] }) {
  const t = useT();
  const tl = t.machinery.labor;
  const qc = useQueryClient();

  const [loteId, setLoteId] = useState('');
  const activeLoteId = loteId || lotes[0]?.loteId || '';
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<LaborForm>(EMPTY_FORM);
  const [error, setError] = useState('');

  const { data: records = [], isLoading } = useQuery({
    queryKey: ['labor', activeLoteId],
    queryFn: () => fetchLaborByLote(activeLoteId),
    enabled: Boolean(activeLoteId),
  });

  const createMut = useMutation({
    mutationFn: () => createLabor(activeLoteId, toInput(form)),
    onSuccess: () => {
      setOpen(false);
      setForm(EMPTY_FORM);
      qc.invalidateQueries({ queryKey: ['labor', activeLoteId] });
    },
    onError: () => setError(tl.saveError),
  });

  const total = useMemo(() => records.reduce((sum, r) => sum + r.totalCost, 0), [records]);
  const set = (patch: Partial<LaborForm>) => setForm(prev => ({ ...prev, ...patch }));
  const loteName = lotes.find(l => l.loteId === activeLoteId)?.name ?? '';

  return (
    <div style={{ marginTop: '24px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
        <Text weight="semibold" size={500}>{tl.title}</Text>
        <Dropdown
          aria-label={tl.lote}
          placeholder={tl.selectLote}
          selectedOptions={activeLoteId ? [activeLoteId] : []}
          value={loteName}
          onOptionSelect={(_, d) => setLoteId(d.optionValue ?? '')}
          style={{ minWidth: '200px' }}
        >
          {lotes.map(l => <Option key={l.loteId} value={l.loteId}>{l.name}</Option>)}
        </Dropdown>
        <Button
          appearance="primary"
          disabled={!activeLoteId}
          onClick={() => { setError(''); setForm(EMPTY_FORM); setOpen(true); }}
        >
          {tl.register}
        </Button>
      </div>

      {!activeLoteId ? (
        <Text style={{ color: tokens.colorNeutralForeground3 }}>{tl.selectLote}</Text>
      ) : isLoading ? (
        <Spinner size="tiny" label={tl.loading} />
      ) : records.length === 0 ? (
        <Text style={{ color: tokens.colorNeutralForeground3 }}>{tl.empty}</Text>
      ) : (
        <>
          <Table size="small" aria-label={tl.title}>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>{tl.date}</TableHeaderCell>
                <TableHeaderCell>{tl.activity}</TableHeaderCell>
                <TableHeaderCell>{tl.laborType}</TableHeaderCell>
                <TableHeaderCell>{tl.workers}</TableHeaderCell>
                <TableHeaderCell>{tl.hoursPerWorker}</TableHeaderCell>
                <TableHeaderCell>{tl.totalCost}</TableHeaderCell>
              </TableRow>
            </TableHeader>
            <TableBody>
              {records.map(r => (
                <TableRow key={r.laborId}>
                  <TableCell>{r.date}</TableCell>
                  <TableCell>{r.activity}</TableCell>
                  <TableCell>{tl.types[r.laborType]}</TableCell>
                  <TableCell>{r.workers}</TableCell>
                  <TableCell>{r.hoursPerWorker}</TableCell>
                  <TableCell>${r.totalCost.toLocaleString('es-MX', { maximumFractionDigits: 2 })}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Text style={{ display: 'block', marginTop: '8px', color: tokens.colorNeutralForeground3 }}>
            {tl.totalLabel}: ${total.toLocaleString('es-MX', { maximumFractionDigits: 2 })}
          </Text>
        </>
      )}

      <Dialog open={open} onOpenChange={(_, d) => { if (!d.open) setOpen(false); }}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>{tl.register}</DialogTitle>
            <DialogContent>
              <Field label={tl.date} required>
                <DateInput value={form.date} onValueChange={iso => set({ date: iso })} />
              </Field>
              <Field label={tl.laborType} required>
                <Dropdown
                  selectedOptions={[form.laborType]}
                  value={tl.types[form.laborType]}
                  onOptionSelect={(_, d) => set({ laborType: (d.optionValue as LaborType) ?? 'JORNAL' })}
                >
                  {LABOR_TYPES.map(lt => <Option key={lt} value={lt}>{tl.types[lt]}</Option>)}
                </Dropdown>
              </Field>
              <Field label={tl.activity} required>
                <Input value={form.activity} onChange={(_, d) => set({ activity: d.value })} />
              </Field>
              <Field label={tl.workers} required>
                <NumberInput value={form.workers} onValueChange={v => set({ workers: v })} />
              </Field>
              <Field label={tl.hoursPerWorker} required>
                <NumberInput value={form.hoursPerWorker} onValueChange={v => set({ hoursPerWorker: v })} />
              </Field>
              <Field label={tl.dailyRate} required>
                <NumberInput value={form.dailyRatePerWorker} onValueChange={v => set({ dailyRatePerWorker: v })} />
              </Field>
              <Field label={tl.notes}>
                <Input value={form.notes} onChange={(_, d) => set({ notes: d.value })} />
              </Field>
              {error && <Text style={{ color: tokens.colorPaletteRedForeground1 }}>{error}</Text>}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setOpen(false)}>{tl.cancel}</Button>
              <Button
                appearance="primary"
                disabled={!isValid(form) || createMut.isPending}
                onClick={() => { setError(''); createMut.mutate(); }}
              >
                {tl.save}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  );
}
